import { useCallback } from "react";
import { useLanguage } from "@/lib/LanguageContext";
import useTenantCurrency from "@/lib/useTenantCurrency";
import { formatMoneyCode, intlLocaleFor } from "../../../lib/formatMoney";

/**
 * The CRM's one money formatter: `(cents, currency?) => string`.
 *
 * The wireframes print "₹15,000"; the rupee is a placeholder for the practice's own
 * currency. A row that carries its own `currency` (contract C0.03) is printed in it —
 * money already charged keeps the currency it was charged in; a figure with none (a
 * summary aggregate) is printed in the tenant's operating currency. While that currency
 * is not known yet the code style of `formatMoneyCode` prints the bare number rather
 * than a guessed code.
 *
 * Whole amounts print without decimals ("$15,000", as the wireframes do); an amount
 * with cents keeps them ("$84.50"). `null`/`undefined` renders the em dash the
 * wireframes use for "no figure".
 */
export default function useCrmMoney() {
  const { lang } = useLanguage();
  const tenantCurrency = useTenantCurrency();
  return useCallback((cents, currency) => {
    if (cents === null || cents === undefined) return "—";
    const n = Number(cents);
    if (!Number.isFinite(n)) return "—";
    const code = String(currency || tenantCurrency || "").trim().toUpperCase();
    if (!code) return formatMoneyCode(n, null, n % 100 === 0 ? 0 : 2);
    const digits = n % 100 === 0 ? 0 : 2;
    try {
      return new Intl.NumberFormat(intlLocaleFor(lang), {
        style: "currency", currency: code, minimumFractionDigits: digits, maximumFractionDigits: digits,
      }).format(n / 100);
    } catch {
      return formatMoneyCode(n, code, digits);
    }
  }, [lang, tenantCurrency]);
}
